import PropTypes from 'prop-types';
import React from 'react';
import memoizeOne from 'memoize-one';

import classNames from 'classnames';

import {Posts} from 'mattermost-redux/constants';
import {isMeMessage as checkIsMeMessage} from 'mattermost-redux/utils/post_utils';

import {makeIsEligibleForClick} from 'utils/utils';
import * as PostUtils from 'utils/post_utils';
import Constants, {A11yCustomEventTypes, AppEvents, Locations} from 'utils/constants';

import PostProfilePicture from 'components/post_profile_picture';
import PostAriaLabelDiv from 'components/post_view/post_aria_label_div';
import PostBody from 'components/post_view/post_body';
import PostHeader from 'components/post_view/post_header';
import PostContext from 'components/post_view/post_context';
import PostPreHeader from 'components/post_view/post_pre_header';
import ThreadFooter from 'components/threading/channel_threads/thread_footer';
import {trackEvent} from 'actions/telemetry_actions';
import EditPost from 'components/edit_post';
import AutoHeightSwitcher, {AutoHeightSlots} from 'components/common/auto_height_switcher';
import {sendToStatist} from '@time-webkit/statist';
import {mark, Marks, markThreadLinkClicked, measure, MeasureNames} from '@time-webkit/performance';
import {PostContextMenu} from 'components/post_menus/post_context_menu/post_context_menu';
import {logPostAction} from 'utils/log_post_action';

// When adding clickable targets within a root post to exclude from post's on click to open thread,
// please add to/maintain the selector below
const isEligibleForClick = makeIsEligibleForClick(
    '.post-image__column, .embed-responsive-item, .hljs, code, input:not(disabled)',
);

export default class Post extends React.PureComponent {
    static get propTypes() {
        return {

            /**
             * The post to render
             */
            post: PropTypes.object.isRequired,

            /**
             * The logged in user ID
             */
            currentUserId: PropTypes.string.isRequired,

            /**
             * Set to center the post
             */
            center: PropTypes.bool,

            /**
             * Set to render post compactly
             */
            compactDisplay: PropTypes.bool,

            /**
             * Set to render a preview of the parent post above this reply
             */
            isFirstReply: PropTypes.bool,

            /**
             * Set to highlight the background of the post
             */
            shouldHighlight: PropTypes.bool,

            /**
             * Set to render this post as if it was attached to the previous post
             */
            consecutivePostByUser: PropTypes.bool,

            /**
             * Set if the previous post is a comment
             */
            previousPostIsComment: PropTypes.bool,

            /*
            * Function called when the post options dropdown is opened
            */
            togglePostMenu: PropTypes.func,

            /**
             * Set to render this comment as a mention
             */
            isCommentMention: PropTypes.bool,

            /**
             * If the post has replies
             */
            hasReplies: PropTypes.bool,

            /**
             * To Check if the current post is last in the list
             */
            isLastPost: PropTypes.bool,

            isBeingEdited: PropTypes.bool,

            /**
             * Whether or not the channel that contains this post is archived
             */
            channelIsArchived: PropTypes.bool.isRequired,

            /**
             * Channel name and type
             */
            channelName: PropTypes.string,

            channelType: PropTypes.string,

            actions: PropTypes.shape({
                selectPost: PropTypes.func.isRequired,
                selectPostCard: PropTypes.func.isRequired,
                markPostAsUnread: PropTypes.func.isRequired,
            }).isRequired,

            /*
            * Set to mark the post as flagged
            */
            isFlagged: PropTypes.bool.isRequired,

            clickToReply: PropTypes.bool,

            newEditorEnabled: PropTypes.bool,

            isSending: PropTypes.bool,
        };
    }

    static defaultProps = {
        post: {},
    };

    constructor(props) {
        super(props);

        this.postRef = React.createRef();

        this.state = {
            showPostContextMenu: false,
            dropdownOpened: false,
            fileDropdownOpened: false,
            hover: false,
            alt: false,
            a11yActive: false,
            ariaHidden: true,
            fadeOutHighlight: false,
        };
    }

    componentDidMount() {
        // Refs can be null when this component is shallowly rendered for testing
        if (this.postRef.current) {
            this.postRef.current.addEventListener(A11yCustomEventTypes.ACTIVATE, this.handleA11yActivateEvent);
            this.postRef.current.addEventListener(A11yCustomEventTypes.DEACTIVATE, this.handleA11yDeactivateEvent);
        }

        if (this.props.shouldHighlight) {
            this.highlightTimeout = setTimeout(() => {
                this.setState({fadeOutHighlight: true});
            }, Constants.PERMALINK_FADEOUT);
        }
    }

    componentWillUnmount() {
        if (this.state.hover) {
            this.removeKeyboardListeners();
        }

        if (this.postRef.current) {
            this.postRef.current.removeEventListener(A11yCustomEventTypes.ACTIVATE, this.handleA11yActivateEvent);
            this.postRef.current.removeEventListener(A11yCustomEventTypes.DEACTIVATE, this.handleA11yDeactivateEvent);
        }

        clearTimeout(this.highlightTimeout);

        if (this.props.isSending) {
            mark(Marks.MESSAGE_SHOWN);
            measure({
                measureName: MeasureNames.SEND_MESSAGE,
                callback: (measureResult) => {
                    sendToStatist('app.messageSend', {
                        channelid: measureResult.channelId,
                        isThread: measureResult.isThread,
                        loadTime: measureResult.loadingTime,
                        requestTime: measureResult.requestTime,
                    });
                },
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.isSending && !this.props.isSending) {
            mark(Marks.MESSAGE_SHOWN);
        }

        if (this.state.a11yActive) {
            this.postRef.current.dispatchEvent(new Event(A11yCustomEventTypes.UPDATE));
        }
    }

    getPostContextValue = memoizeOne(() => {
        return {
            handlePopupOpened: this.handleDropdownOpened,
            location: Locations.CENTER,
            onLogPostAction: this.trackPostAction,
        };
    })

    handleReplyInThreadClick = (e) => {
        e.preventDefault();

        const post = this.props.post;
        if (!post) {
            return;
        }
        this.props.actions.selectPost(post);
    };

    handleCardClick = (post) => {
        if (!post) {
            return;
        }
        this.props.actions.selectPostCard(post);
    };

    handlePostClick = (e) => {
        const {post, clickToReply, isBeingEdited, isSending} = this.props;

        if (!post || post.state === Posts.POST_DELETED) {
            return;
        }

        const isSystemMessage = PostUtils.isSystemMessage(post);
        const fromAutoResponder = PostUtils.fromAutoResponder(post);

        if (
            !e.altKey &&
            clickToReply &&
            (fromAutoResponder || !isSystemMessage) &&
            isEligibleForClick(e) &&
            !isBeingEdited &&
            !isSending
        ) {
            trackEvent('crt', 'clicked_to_reply');
            markThreadLinkClicked({
                detail: {
                    sourceType: 'link',
                    channelId: this.props.post.channel_id,
                    channelName: this.props.channelName,
                    channelType: this.props.channelType,
                },
            });
            this.trackPostAction('replies', 'click');
            this.props.actions.selectPost(post);
        }

        if (this.props.channelIsArchived || post.system_post_ids) {
            return;
        }

        if (e.altKey) {
            this.props.actions.markPostAsUnread(post, 'CENTER');
        }
    };

    handleDropdownOpened = (opened) => {
        if (this.props.togglePostMenu) {
            this.props.togglePostMenu(opened);
        }

        this.setState({
            dropdownOpened: opened,
        });
    };

    handleContextMenuOpened = (opened) => {
        this.setState({showPostContextMenu: opened});
    };

    handleFileDropdownOpened = (opened) => {
        this.setState({
            fileDropdownOpened: opened,
        });
    };

    hasSameRoot = (props) => {
        const post = props.post;

        if (props.isFirstReply) {
            return false;
        } else if (!post.root_id && !props.previousPostIsComment && props.consecutivePostByUser) {
            return true;
        } else if (post.root_id) {
            return true;
        }

        return false;
    };

    handleProfilePopoverOpen = () => {
        this.trackPostAction('profile', 'click');
    }

    getClassName = (post, isSystemMessage, isMeMessage, fromWebhook, fromAutoResponder, fromBot) => {
        let className = 'post';

        if (post.failed && !this.props.isSending) {
            className += ' post--post-failed-new';
        }

        if (post.failed || post.state === Posts.POST_DELETED) {
            className += ' post--hide-controls';
        }

        if (!this.state.fadeOutHighlight && this.props.shouldHighlight) {
            className += ' post--highlight qa_post_highlight';
            if (post.is_pinned || this.props.isFlagged) {
                className += ' post--pinned-or-flagged-highlight';
            }
        }

        let rootUser = '';
        if (this.hasSameRoot(this.props)) {
            rootUser = 'same--root';
        } else {
            rootUser = 'other--root';
        }

        if (fromBot) {
            className += ' post--bot';
        }

        let currentUserCss = '';
        if (this.props.currentUserId === post.user_id && !fromWebhook && !isSystemMessage) {
            currentUserCss = 'current--user';
        }

        let sameUserClass = '';
        if (this.props.consecutivePostByUser) {
            sameUserClass = 'same--user';
        }

        let postType = '';
        if (post.root_id && post.root_id.length > 0) {
            postType = 'post--comment';
        } else if (this.props.hasReplies) {
            postType = 'post--root';
            sameUserClass = '';
            rootUser = '';
        }

        if (isSystemMessage || isMeMessage) {
            className += ' post--system';
            if (isSystemMessage) {
                currentUserCss = '';
                postType = '';
                rootUser = '';
            }
        }

        if (fromAutoResponder) {
            postType = 'post--comment same--root';
        }

        if (this.props.compactDisplay) {
            className += ' post--compact';
        }

        if (
            (this.state.showPostContextMenu || this.state.dropdownOpened || this.state.fileDropdownOpened || this.state.a11yActive) &&
            !this.props.isBeingEdited
        ) {
            className += ' post--hovered';
        }

        if (post.is_pinned || this.props.isFlagged) {
            className += ' post--pinned-or-flagged';
        }

        if (this.props.isBeingEdited) {
            className += ' post--editing';
        }

        if (this.state.alt && !(this.props.channelIsArchived || post.system_post_ids)) {
            className += ' cursor--pointer';
        }

        return className + ' ' + sameUserClass + ' ' + rootUser + ' ' + postType + ' ' + currentUserCss;
    };

    setHover = (e) => {
        this.setState({
            hover: true,
            alt: e.altKey,
        });

        this.addKeyboardListeners();
    };

    unsetHover = () => {
        this.setState({
            hover: false,
            alt: false,
        });

        this.removeKeyboardListeners();
    };

    addKeyboardListeners = () => {
        document.addEventListener('keydown', this.handleAlt);
        document.addEventListener('keyup', this.handleAlt);
    };

    removeKeyboardListeners = () => {
        document.removeEventListener('keydown', this.handleAlt);
        document.removeEventListener('keyup', this.handleAlt);
    };

    handleAlt = (e) => {
        if (this.state.alt !== e.altKey) {
            this.setState({alt: e.altKey});
        }
    };

    handleA11yActivateEvent = () => {
        if (!this.props.isBeingEdited) {
            this.setState({
                a11yActive: true,
                ariaHidden: false,
            });
        }
    };

    handleA11yDeactivateEvent = () => {
        this.setState({
            a11yActive: false,
            ariaHidden: true,
        });
    };

    handleCopy = () => {
        this.trackPostAction('copy', 'rightClick');
    };

    trackPostAction = (action, inputType) => {
        logPostAction(action, inputType, Locations.CENTER, this.props.post);
    }

    render() {
        const {post, hasReplies, compactDisplay, isBeingEdited, newEditorEnabled} = this.props;

        if (!post.id) {
            return null;
        }

        const isSystemMessage = PostUtils.isSystemMessage(post);
        const isMeMessage = checkIsMeMessage(post);
        const fromAutoResponder = PostUtils.fromAutoResponder(post);
        const fromWebhook = post && post.props && post.props.from_webhook === 'true';
        const fromBot = post && post.props && post.props.from_bot === 'true';
        const hasSameRoot = this.hasSameRoot(this.props);

        let profilePic;
        const hideProfilePicture =
            hasSameRoot &&
            this.props.consecutivePostByUser &&
            !post.root_id &&
            !hasReplies &&
            !fromBot;
        if (!hideProfilePicture) {
            profilePic = (
                <PostProfilePicture
                    compactDisplay={this.props.compactDisplay}
                    post={post}
                    userId={post.user_id}
                    onProfilePopoverOpen={this.handleProfilePopoverOpen}
                />
            );

            if (fromAutoResponder) {
                profilePic = <span className='auto-responder'>{profilePic}</span>;
            }
        }

        let centerClass = '';
        if (this.props.center) {
            centerClass = 'center';
        }

        const hasPaddingForInfo = post.props?.card && this.props.consecutivePostByUser;

        const postHeader = (
            <PostHeader
                post={post}
                handleReplyInThreadClick={this.handleReplyInThreadClick}
                handleCardClick={this.handleCardClick}
                handleDropdownOpened={this.handleDropdownOpened}
                compactDisplay={this.props.compactDisplay}
                isFirstReply={this.props.isFirstReply}
                showTimeWithoutHover={!hideProfilePicture}
                hover={
                    (
                        this.state.hover ||
                        this.state.a11yActive ||
                        this.state.fileDropdownOpened ||
                        this.state.dropdownOpened ||
                        this.state.showPostContextMenu
                    ) && !this.props.isBeingEdited
                }
                isLastPost={this.props.isLastPost}
                isSending={this.props.isSending}
                onProfilePopoverOpen={this.handleProfilePopoverOpen}
            />
        );

        const postBody = (
            <PostBody
                post={post}
                handleCommentClick={this.handleReplyInThreadClick}
                compactDisplay={this.props.compactDisplay}
                isCommentMention={this.props.isCommentMention}
                isFirstReply={this.props.isFirstReply}
                handleFileDropdownOpened={this.handleFileDropdownOpened}
            />
        );

        const threadFooter = post.root_id ? null : <ThreadFooter threadId={post.id} />;

        const slot1 = (
            <>
                {compactDisplay && postHeader}
                {postBody}
            </>
        );

        const slot2 = (
            <>
                {compactDisplay && postHeader}
                {compactDisplay && isBeingEdited && <div className={'clearfix'} />}
                {!newEditorEnabled && <EditPost />}
            </>
        );

        const showSlot = isBeingEdited ? AutoHeightSlots.SLOT2 : AutoHeightSlots.SLOT1;

        return (
            <PostContext.Provider value={this.getPostContextValue()}>
                <PostContextMenu
                    post={post}
                    open={this.state.showPostContextMenu}
                    disabled={!this.state.hover && !this.state.showPostContextMenu}
                    onToggle={this.handleContextMenuOpened}
                >
                    <PostAriaLabelDiv
                        ref={this.postRef}
                        id={'post_' + post.id}
                        data-testid='postView'
                        role='listitem'
                        className={`a11y__section ${this.getClassName(
                            post,
                            isSystemMessage,
                            isMeMessage,
                            fromWebhook,
                            fromAutoResponder,
                            fromBot,
                        )}`}
                        tabIndex='0'
                        onMouseOver={this.setHover}
                        onMouseLeave={this.unsetHover}
                        onTouchStart={this.setHover}
                        onClick={this.handlePostClick}
                        onCopy={this.handleCopy}
                        aria-atomic={true}
                        post={post}
                        data-qa='post_id'
                    >
                        <PostPreHeader
                            isFlagged={this.props.isFlagged}
                            isPinned={post.is_pinned}
                            channelId={post.channel_id}
                        />
                        <div
                            role='application'
                            data-testid='postContent'
                            className={classNames('post__content', centerClass, {
                                'post__with-info': hasPaddingForInfo,
                            })}
                            aria-hidden={this.state.ariaHidden}
                            data-qa='post_content'
                        >
                            <div className='post__img'>{profilePic}</div>
                            <div>
                                {!compactDisplay && postHeader}
                                <AutoHeightSwitcher
                                    showSlot={showSlot}
                                    slot1={slot1}
                                    slot2={slot2}
                                    onTransitionEnd={() => document.dispatchEvent(new Event(AppEvents.FOCUS_EDIT_TEXTBOX))}
                                />
                                {threadFooter}
                            </div>
                        </div>
                    </PostAriaLabelDiv>
                </PostContextMenu>
            </PostContext.Provider>
        );
    }
}
